import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getRoles, destroyRole } from '../../../api/RolesAPI';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppPaginator from '../../../components/AppPaginator';
import AppSearchBox from '../../../components/AppSearchBox';
import PerPageBlock from '../../../components/common/PerPageBlock';
import AppToaster from '../../../components/AppToaster';

const RolesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [rolesList, setRolesList] = useState({ data: [], last_page: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [triggerFetch, setTriggerFetch] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success!', message: successMessage });
            navigate(location.pathname, { replace: true });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        setIsLoading(true);
        const filters = { page: currentPage, per_page: perPage, search_term: searchTermCompleted };
        getRoles(filters, (response) => {
            setIsLoading(false);
            if (response.roles) {
                setRolesList(response.roles);
            }
        });
    }, [currentPage, perPage, searchTermCompleted, triggerFetch]);

    const handleAddClick = () => navigate('/club_portal/roles/create');
    const handleEditClick = (id) => navigate(`/club_portal/roles/edit/${id}`);

    const handleDeleteClick = (role) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>Delete role: <strong>${role.name}</strong>?</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroyRole(role, (response) => {
                    if (response.role) {
                        Swal.fire('Deleted!', 'The role has been deleted.', 'success');
                        setTriggerFetch(p => !p);
                    } else {
                        Swal.fire('Error!', 'Could not delete role.', 'error');
                    }
                });
            }
        });
    };

    return (
        <main>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title="Roles" />
                <div className="row">
                    <div className="col-12">
                        <div className="tab-wrapper mb-3">
                            <ul className="tabs">
                                <li className="tab-link active">All Roles</li>
                                <li className="ms-md-auto">
                                    <div className="text-md-end d-flex flex-column flex-md-row align-items-md-center">
                                        <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                        <button className="text-nowrap btn btn-primary btn-sm mt-2 mt-md-0 ms-md-3" onClick={handleAddClick}>
                                            <i className="ti ti-plus f-s-18"></i> Add Role
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div className="content-wrapper">
                            <div className="d-flex m-md-3">
                                {rolesList.data.length > 0 && (
                                    <>
                                        <AppPaginator totalPages={rolesList.last_page} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                                        <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                    </>
                                )}
                                {isLoading && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                            </div>
                            <div className="tabs-content active">
                                <div className="row">
                                    {!isLoading && (
                                        rolesList.data.length > 0 ? rolesList.data.map(role => (
                                            <div className="col-md-6 col-xl-4 project-card" key={role.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            <div className="h-40 w-40 d-flex-center b-r-50 bg-light-primary text-primary">
                                                                <i className="fas fa-user-tag fa-lg"></i>
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600">{role.name}</h6>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button">
                                                                    <i className="ti ti-dots-vertical text-dark" />
                                                                </button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(role.id)}><i className="ti ti-edit text-success" /> Edit</a></li>
                                                                    <li><a className="dropdown-item" onClick={() => handleDeleteClick(role)}><i className="ti ti-trash text-danger" /> Delete</a></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body">
                                                        <span className="badge bg-light-primary">{role.permissions_count} Permission(s)</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <div className="card mb-3">
                                                <div className="row g-0">
                                                    <div className="col-md-8">
                                                        <div className="card-body">
                                                            <h5 className="card-title">No Roles Found</h5>
                                                            <p className="card-text">Click "Add Role" to create a new role and assign permissions.</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <img alt="No data illustration" className="img-fluid" src="/assets/images/blog-app/09.jpg" />
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default RolesPage;

